import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bookingApi } from '../../../utils/bookingApi';
import { formatDate, formatDateTime, formatDayDate } from '../../../utils/dates';
import { PageHeader, Sheet, Skeleton, ErrorState, useConfirm, useToast } from '../../../components/ui';
import { INCIDENT_TYPE_LABEL, INCIDENTS_PATH, SeverityPill } from './AdminIncidents';
import '../booking-shared.css';
import './Safeguarding.css';

const EMPTY_FORWARD = { toEmail: '', toName: '', note: '' };

// Send a copy of the report to someone outside the app, such as a school
function ForwardSheet({ incidentId, onClose, onForwarded }) {
  const [form, setForm] = useState(EMPTY_FORWARD);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    if (!/^\S+@\S+\.\S+$/.test(form.toEmail.trim())) {
      setEmailError('Enter an email address');
      setError('Check the email address.');
      return;
    }
    setEmailError(null);
    setSubmitting(true);
    setError(null);
    try {
      await bookingApi.forwardIncident(incidentId, form);
      onForwarded();
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't send the report. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <Sheet
      title="Forward report"
      onClose={onClose}
      dirty={JSON.stringify(form) !== JSON.stringify(EMPTY_FORWARD)}
      onSubmit={handleSubmit}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send'}
          </button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      <div className="sg-form">
        <label className="bk-label">
          Their email address
          <input type="email" className="bk-input" value={form.toEmail} onChange={set('toEmail')}
            placeholder="For example, office@school.co.uk" aria-invalid={emailError ? 'true' : undefined} />
          {emailError && <span className="bk-field-error">{emailError}</span>}
        </label>
        <label className="bk-label">
          Their name<span className="bk-optional"> (optional)</span>
          <input type="text" className="bk-input" value={form.toName} onChange={set('toName')} placeholder="For example, school office" />
        </label>
        <label className="bk-label">
          Note<span className="bk-optional"> (optional)</span>
          <input type="text" maxLength={300} className="bk-input" value={form.note} onChange={set('note')} />
        </label>
        {error && <p className="bk-form-error" role="alert">{error}</p>}
      </div>
    </Sheet>
  );
}

function Section({ title, children }) {
  return (
    <section className="sg-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

// One first aid report on its own page
export default function AdminIncidentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const [current, setCurrent] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState(null);
  const [forwarding, setForwarding] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    return bookingApi.getIncident(id)
      .then(res => setCurrent(res.data))
      .catch((err) => { console.error('Could not load the first aid report', err); setLoadError(true); });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Delete this first aid report?',
      message: "It's removed for good, with its forwarding history.",
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      await bookingApi.deleteIncident(id);
      toast('First aid report deleted');
      navigate(INCIDENTS_PATH, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't delete the report. Try again.");
      setDeleting(false);
    }
  };

  const back = { to: INCIDENTS_PATH, label: 'First aid reports' };

  if (loadError) {
    return (
      <div className="bk-page bk-page--narrow">
        <PageHeader back={back} title="First aid report" />
        <ErrorState title="Couldn't load the report" onRetry={load} />
      </div>
    );
  }
  if (!current) {
    return (
      <div className="bk-page bk-page--narrow">
        <PageHeader back={back} title="First aid report" />
        <Skeleton rows={4} label="Loading the report…" />
      </div>
    );
  }

  const name = current.gymnast ? `${current.gymnast.firstName} ${current.gymnast.lastName}` : 'Unknown gymnast';
  const forwards = current.forwards || [];

  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader
        back={back}
        title="First aid report"
        details={`${name} · ${formatDayDate(current.incidentDate)}`}
        action={{ label: 'Edit', to: `${INCIDENTS_PATH}/${id}/edit` }}
      />

      {error && <p className="bk-form-error" role="alert">{error}</p>}

      <div className="bk-card">
        <dl className="sg-facts">
          <div><dt>Type</dt><dd>{INCIDENT_TYPE_LABEL[current.incidentType] ?? current.incidentType}</dd></div>
          <div><dt>Severity</dt><dd><SeverityPill severity={current.severity} /></dd></div>
          {current.location && <div><dt>Location</dt><dd>{current.location}</dd></div>}
          <div><dt>Reported by</dt><dd>{current.reportedBy.firstName} {current.reportedBy.lastName}</dd></div>
        </dl>

        <Section title="What happened"><p>{current.description}</p></Section>
        {current.injuryDetails && <Section title="Injury or symptoms"><p>{current.injuryDetails}</p></Section>}
        {current.firstAidGiven && <Section title="First aid given"><p>{current.firstAidGiven}</p></Section>}
        {current.outcome && <Section title="Outcome"><p>{current.outcome}</p></Section>}
        {(current.witnessName || current.witnessContact || current.witnessStatement) && (
          <Section title="Witness account">
            {current.witnessName && <p className="sg-line"><strong>Name:</strong> {current.witnessName}</p>}
            {current.witnessContact && <p className="sg-line"><strong>Contact:</strong> {current.witnessContact}</p>}
            {current.witnessStatement && <p>{current.witnessStatement}</p>}
          </Section>
        )}

        <p className="sg-note">
          {current.adultNotifiedAt
            ? `The family was emailed on ${formatDateTime(current.adultNotifiedAt)}.`
            : 'The family is emailed an hour after the report is filed.'}
        </p>
      </div>

      <div className="bk-card">
        <Section title="Forwarded to">
          {forwards.length === 0 ? (
            <p className="bk-muted">Not forwarded to anyone yet.</p>
          ) : (
            <ul className="sg-forwards">
              {forwards.map(fw => (
                <li key={fw.id}>
                  <div>{fw.toName ? `${fw.toName} ` : ''}<span className="bk-muted">{fw.toEmail}</span></div>
                  {fw.note && <div className="sg-forwards__meta">{fw.note}</div>}
                  <div className="sg-forwards__meta">
                    Sent by {fw.forwardedBy.firstName} {fw.forwardedBy.lastName} on {formatDate(fw.sentAt)}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="bk-actions">
            <button type="button" className="bk-btn" onClick={() => setForwarding(true)}>Forward report</button>
          </div>
        </Section>
      </div>

      <div className="bk-actions">
        <button type="button" className="bk-btn bk-btn--outline-danger" onClick={handleDelete} disabled={deleting}>
          {deleting ? 'Deleting…' : 'Delete report'}
        </button>
      </div>

      {forwarding && (
        <ForwardSheet
          incidentId={current.id}
          onClose={() => setForwarding(false)}
          onForwarded={() => { setForwarding(false); toast('Report sent'); load(); }}
        />
      )}
    </div>
  );
}
